import { useCallback, useRef, useState } from 'react';
import { toast } from '../../common/toast';
import SelectInventorySaleDialog from './SelectInventorySaleDialog';

const hasSale = (row) => {
  const txn = row?.TransactionID ?? row?.trans_id;
  if (txn != null && String(txn).trim() !== '') return true;
  const price = Number(String(row?.SalePrice ?? '').replace(/[,$]/g, ''));
  return Number.isFinite(price) && price > 0;
};

const toastUnsold = () => toast.error('This property has not sold and cannot be added', { autoClose: false });

/**
 * Promise-based multi-sale picker for inventory lookup responses.
 * Single row → resolves immediately; multiple → opens SelectInventorySaleDialog.
 */
export function useSelectInventorySale() {
  const [picker, setPicker] = useState(null); // { rows, resolve }
  const pickerRef = useRef(null);
  pickerRef.current = picker;

  const pickSale = useCallback((rows, requireSale = false) => {
    if (!rows?.length) {
      if (requireSale) toastUnsold();
      return Promise.resolve(null);
    }
    const normalized = rows.map((row) => ({
      ...row,
      TransactionID: row.TransactionID ?? row.trans_id ?? null,
    }));
    const candidates = requireSale ? normalized.filter(hasSale) : normalized;
    if (requireSale && !candidates.length) {
      toastUnsold();
      return Promise.resolve(null);
    }
    if (candidates.length === 1) return Promise.resolve(candidates[0]);
    return new Promise((resolve) => {
      setPicker({ rows: candidates, resolve });
    });
  }, []);

  const finish = useCallback((row) => {
    const current = pickerRef.current;
    if (!current) return;
    current.resolve(row);
    setPicker(null);
  }, []);

  const dialog = (
    <SelectInventorySaleDialog
      open={Boolean(picker)}
      rows={picker?.rows || []}
      onSelect={(row) => finish(row)}
      onCancel={() => finish(null)}
    />
  );

  return { pickSale, dialog };
}
